import { redirect } from "@tanstack/react-router";
import { readAuthSnapshot, type AuthState, type Role } from "./authStore";

/**
 * Route guards pro TanStack Router `beforeLoad` per ANALYZE §5.2 routing tree.
 *
 * Use cases:
 *   - `routes/admin/_layout.tsx` → `beforeLoad: requireAdmin`
 *   - `routes/tenant/_layout.tsx` → `beforeLoad: requireTenant`
 *   - Specifické routes (např. UC-006 `microgroup`) přidají vlastní role probe
 *     uvnitř route s `requireRole(['subAdmin'])`.
 *
 * Chování:
 *   - `state="loading"` — propagujeme bez redirectu (rendering bude blokován
 *     `<RouterProvider defaultPendingComponent={<Spinner/>}>`).
 *   - `state="anonymous"` — redirect na `/login?next={current-url}`.
 *   - `state="authenticated"` + wrong role — redirect na `/` (root index
 *     rozhodne podle skutečné role per `__root.tsx` post-auth landing).
 *
 * REQ-AUTH-016: bez JWT cookie navigace na chráněnou cestu → /login.
 * REQ-AUTH-014: role mismatch → opt-out z chráněné sekce.
 *
 * TEST-010 (admin-route-with-tenant-jwt): tenant JWT na /admin/* → 403 fallback
 * — realizováno přes redirect na `/` index (kde root layout zobrazí
 * "Access denied" message pro non-globalAdmin pokoušející se admin URL).
 *
 * Per REQ-AUTH-018: pokud URL obsahuje query param `?token=` (consume token z
 * email magic linku), root layout consumuje hodnotu PŘED tímto guardem a
 * volá `history.replaceState()` (viz `routes/__root.tsx`).
 */

export interface GuardContext {
  location: { href: string };
}

function buildNextUrl(ctx: GuardContext): string {
  return encodeURIComponent(ctx.location.href);
}

/** Vyžaduje globalAdmin role. */
export function requireAdmin(ctx: GuardContext): void {
  const auth = readAuthSnapshot();
  enforceState(auth, ctx, (state) => {
    if (state.user.role !== "globalAdmin") {
      throw redirect({ to: "/", search: { reason: "forbidden_admin" } });
    }
  });
}

/** Vyžaduje libovolnou tenant role (tenantAdmin / subAdmin / endUser). */
export function requireTenant(ctx: GuardContext): void {
  const auth = readAuthSnapshot();
  enforceState(auth, ctx, (state) => {
    const r = state.user.role;
    if (r !== "tenantAdmin" && r !== "subAdmin" && r !== "endUser") {
      throw redirect({ to: "/", search: { reason: "forbidden_tenant" } });
    }
  });
}

/** Vyžaduje konkrétní role(s). */
export function requireRole(
  allowed: Role[],
): (ctx: GuardContext) => void {
  return function guard(ctx) {
    const auth = readAuthSnapshot();
    enforceState(auth, ctx, (state) => {
      if (!allowed.includes(state.user.role)) {
        throw redirect({ to: "/", search: { reason: "forbidden_role" } });
      }
    });
  };
}

function enforceState(
  auth: AuthState,
  ctx: GuardContext,
  onAuthenticated: (state: Extract<AuthState, { state: "authenticated" }>) => void,
): void {
  if (auth.state === "loading") {
    // Loading je transient; nepropagujeme redirect aby /admin/* refresh nepřišel
    // o cílovou URL během /api/auth/me bootstrap fetch. Guard čeká.
    return;
  }
  if (auth.state === "anonymous") {
    throw redirect({
      to: "/login",
      search: { next: buildNextUrl(ctx) },
    });
  }
  // Re-prompt force redirect před cokoliv jiného (DEC-013 Policy C).
  if (auth.user.requiresConsentRePrompt) {
    throw redirect({ to: "/consent/re-prompt" });
  }
  onAuthenticated(auth);
}
